import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import UserMenu from '@components/UserMenu/UserMenu';
import MainMenu from '@features/Header/components/MainMenu';
import { useGetUserQuery } from '@services/authApi';
import { useTranslation } from 'react-i18next';
import { Grid2, useTheme } from '@mui/material';
import { UserState } from '@features/Auth/user.slice';
import { useSelector } from 'react-redux';
import { RootState } from '@redux/store';
import HamburgerMenu from './HamburgerMenu/HamburgerMenu';
import LanguageDropDownMenu from '../components/LanguageDropDownMenu';
import { Pages } from '../constants/pages';
import Language from '../components/Language';
import { UserMenuItems } from '../constants/userMenuItems';
import { ICountryInfo } from '../utils/languageHelper';
import { Locale } from 'types/locale';
import HertzLogoType from '@components/HertzLogoType/HertzLogoType';

interface HeaderBarProps {
  isAlert: boolean;
  displayedLanguageInfo: ICountryInfo;
  locale: Locale;
  setLocale: (value: React.SetStateAction<Locale>) => void;
  closeLanguageMenu: () => void;
  openLanguageMenu: (event: React.MouseEvent<HTMLElement>) => void;
  anchorEl: HTMLElement;
}

const HeaderBar: React.FC<HeaderBarProps> = ({
  isAlert,
  displayedLanguageInfo,
  locale,
  setLocale,
  closeLanguageMenu,
  openLanguageMenu,
  anchorEl,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const userState: UserState = useSelector((state: RootState) => state.user);
  const isSignedIn = !!userState?.user?.signedIn;
  const { data: user } = useGetUserQuery(undefined, {
    skip: !isSignedIn,
  });

  const styles = {
    appBar: {
      width: 1,
      alignItems: 'center',
      backgroundColor: `${theme.palette.white.main} !important`,
      borderBottom: `solid 5px ${theme.palette.accent.main}`,
      position: 'fixed',
    },
    toolbar: {
      maxWidth: '1920px',
      minHeight: 'auto !important',
      width: {
        xs: 1,
        md: '85%',
      },
      px: {
        xs: '1.5rem',
        md: 0,
      },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    gridRootContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      height: '3.5rem',
      width: 1,
    },
    mainMenuContainer: {
      display: { xs: 'none', lg: 'block' },
      flexGrow: 1,
    },
  };

  return (
    <AppBar sx={styles.appBar} elevation={isAlert ? 0 : 4}>
      <Toolbar disableGutters id="header-toolbar" sx={styles.toolbar}>
        <Grid2 container sx={styles.gridRootContainer}>
          <HertzLogoType />
          

          <Grid2 sx={styles.mainMenuContainer}>
            <MainMenu links={Pages(t)} />
          </Grid2>

          {isSignedIn && user ? (
            <Grid2 display={{ xs: 'none', lg: 'block' }}>
              <UserMenu
                username={user.name}
                accountName={user.username}
                menuItems={UserMenuItems(
                  t,
                  displayedLanguageInfo,
                  user,
                  locale,
                  setLocale,
                  closeLanguageMenu
                )}
              />
            </Grid2>
          ) : (
            <Language
              displayedLanguageInfo={displayedLanguageInfo}
              openLanguageMenu={openLanguageMenu}
            />
          )}

          {isSignedIn && user ? (
            <HamburgerMenu
              pages={Pages(t)}
              userMenuItems={UserMenuItems(
                t,
                displayedLanguageInfo,
                user,
                locale,
                setLocale,
                closeLanguageMenu
              )}
              userName={user.name}
              accountName={user.username}
            />
          ) : null}
        </Grid2>
      </Toolbar>

      <LanguageDropDownMenu
        anchorEl={anchorEl}
        user={user}
        locale={locale}
        setLocale={setLocale}
        closeLanguageMenu={closeLanguageMenu}
      />
    </AppBar>
  );
};

export default HeaderBar;
